import {
	__experimentalToolsPanelItem as ToolsPanelItem,
	__experimentalToggleGroupControl as ToggleGroupControl,
	__experimentalToggleGroupControlOption as ToggleGroupControlOption,
} from '@wordpress/components';
import { useMemo } from '@wordpress/element';
import type { ReactNode } from 'react';
import { __, _x } from '@wordpress/i18n';
import type { DimensionsToolOption, ScaleToolProps } from './types';

/**
 * The descriptions are purposely made generic as object-fit could be used for
 * any replaced element. Provide your own set of options if you need different
 * help text or labels.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/Replaced_element
 */
const DEFAULT_SCALE_OPTIONS: DimensionsToolOption[] = [
	{
		value: 'fill',
		label: _x( 'Fill', 'Scale option for dimensions control' ),
		help: __( 'Fill the space by stretching the content.' ),
	},
	{
		value: 'contain',
		label: _x( 'Contain', 'Scale option for dimensions control' ),
		help: __( 'Fit the content to the space without clipping.' ),
	},
	{
		value: 'cover',
		label: _x( 'Cover', 'Scale option for dimensions control' ),
		help: __( "Fill the space by clipping what doesn't fit." ),
	},
	{
		value: 'none',
		label: _x( 'None', 'Scale option for dimensions control' ),
		help: __(
			'Do not adjust the sizing of the content. Content that is too large will be clipped, and content that is too small will have additional padding.'
		),
	},
	{
		value: 'scale-down',
		label: _x( 'Scale down', 'Scale option for dimensions control' ),
		help: __(
			'Scale down the content to fit the space if it is too big. Content that is too small will have additional padding.'
		),
	},
];

/**
 * A tool to select the CSS object-fit property for the image.
 *
 * @param {ScaleToolProps} props The component props.
 *
 * @return The scale tool.
 */
export default function ScaleTool( {
	panelId,
	value,
	onChange,
	options = DEFAULT_SCALE_OPTIONS,
	defaultValue = DEFAULT_SCALE_OPTIONS[ 0 ].value,
	isShownByDefault = true,
}: ScaleToolProps ) {
	const displayValue = value ?? defaultValue;

	const scaleHelp = useMemo( () => {
		return options.reduce(
			( acc, option ) => {
				acc[ option.value ] = option.help;
				return acc;
				// The reducer fills the empty object.
			},
			{} as Record< string, ReactNode >
		);
	}, [ options ] );

	return (
		<ToolsPanelItem
			label={ _x( 'Scale', 'Image scaling options' ) }
			isShownByDefault={ isShownByDefault }
			hasValue={ () => displayValue !== defaultValue }
			onDeselect={ () => onChange( defaultValue ) }
			panelId={ panelId }
		>
			<ToggleGroupControl
				label={ _x( 'Scale', 'Image scaling options' ) }
				isBlock
				help={ scaleHelp[ displayValue ] }
				value={ displayValue }
				// The options' values are strings.
				onChange={ onChange as ( value?: string | number ) => void }
			>
				{ options.map( ( option ) => (
					<ToggleGroupControlOption
						key={ option.value }
						{ ...option }
					/>
				) ) }
			</ToggleGroupControl>
		</ToolsPanelItem>
	);
}
